import React from "react";

import {
  faChevronRight,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin, twMerge } from "tailwind-merge";

import { BetaPill } from "src/base-components/BetaPill";
import { ManifestVersionPill } from "src/connections/ManifestVersionPill";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";

export type DisabledDetails =
  | { disabled?: false; disabledInfo?: never }
  | { disabled: true; disabledInfo: { title: string; body?: string } };

type Props = {
  title: string;
  description: string;
  onClick: () => void;
  dataLoc?: string;
  nodeIcon: React.ReactNode;
  isBeta?: boolean;
  infoBody?: React.ReactNode;
  infoTitle?: React.ReactNode;
  isGroup?: boolean;
  manifestVersion?: string;
} & DisabledDetails;

export const AddNodeCard: React.FC<Props> = ({
  title,
  description,
  onClick,
  dataLoc,
  nodeIcon,
  disabled = false,
  disabledInfo,
  isBeta,
  infoBody,
  infoTitle,
  isGroup = false,
  manifestVersion,
}) => {
  const showManifestVersion = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.showManifestVersion,
  );
  return (
    <Tooltip
      body={disabledInfo?.body}
      disabled={!disabled}
      placement="top"
      title={disabledInfo?.title}
      asChild
    >
      <div
        className={twMerge(
          "my-4 h-full w-full rounded-lg px-2 py-2 transition-all",
          !disabled &&
            "border border-gray-200 hover:cursor-pointer hover:border-indigo-400",
          disabled && "cursor-not-allowed bg-gray-50",
        )}
        data-loc={dataLoc}
        onClick={onClick}
      >
        <div className="flex justify-between">
          <div className="flex items-center">
            <div className={twJoin("h-10 w-10", disabled && "opacity-50")}>
              {nodeIcon}
            </div>
            <div className="ml-2.5 flex flex-col justify-center">
              <div
                className={twMerge(
                  "flex items-center gap-2 font-inter-semibold-13px",
                  !disabled && "text-gray-800",
                  disabled && "text-gray-500",
                )}
                data-loc={`${dataLoc}-name`}
              >
                {title}
                {showManifestVersion && manifestVersion && (
                  <ManifestVersionPill
                    dataLoc="add-node-manifest-version"
                    version={manifestVersion}
                  />
                )}
              </div>
              <div
                className={twMerge(
                  "font-inter-normal-12px",
                  !disabled && "text-gray-500",
                  disabled && "text-gray-400",
                )}
              >
                {description}
              </div>
            </div>
          </div>
          {isBeta && (
            <div className="ml-auto self-start">
              <BetaPill placement="bottom" />
            </div>
          )}
          {(infoBody || infoTitle) && (
            <Tooltip
              body={infoBody}
              disabled={!infoBody && !infoTitle}
              placement="top-start"
              title={infoTitle}
              asChild
            >
              <Icon icon={faInfoCircle} size="md" />
            </Tooltip>
          )}
          {isGroup && (
            <div className="ml-auto flex items-center">
              <Icon icon={faChevronRight} size="sm" />
            </div>
          )}
        </div>
      </div>
    </Tooltip>
  );
};
